import type { CSSProperties } from 'react';
import type { TFunction } from 'i18next';
import i18n from 'i18next';
import { ExcelTransactionSourceType } from 'store/app/enums';
import { showsCoaAccountCodeColumn } from 'utils/get-provider-type';
import { VALIDATION_COLUMN_KEY_MAP } from '../upload-transactions-columns-modal';
export { PROVIDER_ICON_SRC as providerIcons } from 'utils/resolve-provider-icon-src';

/** Toolbar / activity-log icon grey (matches `iconBell` / `iconActivity.svg`). */
export const ENTITY_TOOLBAR_MUTED_ICON_COLOR = 'var(--color-text-muted, #5a6a6a)';

export const ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY =
  '_entityTransactionActions';
export const ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY =
  '_entityTransactionAttachment';
export const ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY =
  '_entityTransactionStatus';

/** Synthetic table keys (not yet on Excel row model); placeholders until API fields are wired. */
export const TRACKING_CATEGORY_1_COLUMN_KEY = 'trackingCategory1';
export const TRACKING_CATEGORY_2_COLUMN_KEY = 'trackingCategory2';
export const ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS: readonly string[] =
  [TRACKING_CATEGORY_1_COLUMN_KEY, TRACKING_CATEGORY_2_COLUMN_KEY];

/** Not shown in the transactions table or column-customize drawer (still on row model / edit drawer excluded). */
export const ENTITY_TRANSACTIONS_TABLE_EXCLUDED_COLUMN_KEYS = new Set<string>([
  'vatNumber',
  'country',
]);

export function stripExcludedEntityTransactionTableColumns(
  columnKeys: string[]
): string[] {
  return columnKeys.filter(
    (k) => !ENTITY_TRANSACTIONS_TABLE_EXCLUDED_COLUMN_KEYS.has(k)
  );
}

/** Preference: when true, open edit drawer when clicking attachment; when false, show preview only (edit in table). */
export const ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF =
  'entity-transactions-edit-in-drawer';

/** Last selected bank account for Ledger-plan provider posting (per entity). */
export const LEDGER_POST_BANK_ACCOUNT_PREF = 'ledger-post-bank-account';

/** Default editing-style when the user has saved “all entities”; entity key overrides when explicitly set. */
export const ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF_ALL_ENTITIES_KEY =
  'entity-transactions-edit-in-drawer-all-entities';

/** Default columns when the user has saved “all entities”; per-entity key overrides when columns are stored. */
export const TRANSACTIONS_TAB_COLUMNS_CONFIG_ALL_ENTITIES_KEY =
  'transactions-tab-columns-config-all-entities';

/** User layout for transaction review/edit drawer (density + column count). */
export const ENTITY_TRANSACTION_EDIT_DRAWER_LAYOUT_PREF =
  'entity-transaction-edit-drawer-layout';

/** Teal accent for file-state tabs (brand primary; high contrast darkens via `--color-primary`). */
export const TRANSACTIONS_FILE_STATE_TAB_TEAL =
  'var(--color-primary, #41aaaf)';
export const TRANSACTIONS_FILE_STATE_TAB_CONNECTOR = '#E0E0E0';

/** Horizontal connectors between view steps — grow/shrink‑safe so lines stay visibly long. */
export const TRANSACTIONS_FILE_STATE_TAB_CONNECTOR_BAR: CSSProperties = {
  flex: '1 0 30px',
  minWidth: 30,
  height: 1,
  alignSelf: 'center',
  backgroundColor: TRANSACTIONS_FILE_STATE_TAB_CONNECTOR,
};

/** Column keys whose edit input should be numeric-only, formatted to 2 d.p. */
export const NUMERIC_COLUMN_KEYS = new Set([
  'gross',
  'net',
  'tax',
  'taxRate',
  'exchangeRate',
  'discountAmount',
  'quantity',
  'unitPrice',
]);

/** Optional numeric fields: empty input clears to null (not 0). */
export const OPTIONAL_NUMERIC_COLUMN_KEYS = new Set([
  'exchangeRate',
  'discountAmount',
  'quantity',
  'unitPrice',
]);

/** Money / price fields: shown with dot decimal, rounded to max 2 fractional digits. */
export const TRANSACTION_AMOUNT_FIELD_KEYS = new Set([
  'gross',
  'net',
  'tax',
  'unitPrice',
  'discountAmount',
  'exchangeRate',
]);

/** Quantity + unit price (Xero / QuickBooks / Zoho / Receipts transactions table). */
export const QUANTITY_UNIT_PRICE_COLUMN_KEYS = [
  'quantity',
  'unitPrice',
] as const;

/** Quantity may be a decimal up to this many places (Xero / QBO / Zoho / Receipts). */
export const QUANTITY_MAX_DECIMAL_PLACES = 4;

/** Sanitise quantity input: digits, one decimal point, max fractional digits. */
export function sanitiseQuantityInput(raw: string): string {
  const sanitised = raw
    .replace(/,/g, '.')
    .replace(/[^0-9.\-]/g, '')
    .replace(/(?!^)-/g, '')
    .replace(/(\..*)\./g, '$1');
  const dotIdx = sanitised.indexOf('.');
  if (dotIdx === -1) return sanitised;
  const intPart = sanitised.slice(0, dotIdx);
  const decPart = sanitised.slice(dotIdx + 1);
  if (decPart.length <= QUANTITY_MAX_DECIMAL_PLACES) return sanitised;
  return `${intPart}.${decPart.slice(0, QUANTITY_MAX_DECIMAL_PLACES)}`;
}

/**
 * Fields shared across all lines in a grouped document drawer (receipts / same file).
 * Edited once at the top and applied to every line on save.
 */
export const DOCUMENT_GROUP_SHARED_DRAWER_KEYS = [
  'transactionDate',
  'uploadDate',
  'dueDate',
  'currency',
  'exchangeRate',
  'sourceType',
  'transactionNumber',
  'contactName',
  'reference',
  'taxInclusive',
] as const;

export type DocumentGroupSharedDrawerKey =
  (typeof DOCUMENT_GROUP_SHARED_DRAWER_KEYS)[number];

/** Hidden in review/edit drawer; original values kept on drawer save. */
export const DRAWER_EXCLUDED_FIELD_KEYS = new Set<string>([
  'vatNumber',
  'country',
]);

/** Field order within each line (grouped document drawer). */
export const DRAWER_PER_LINE_FIELD_ORDER = [
  'description',
  'accountCode',
  'taxName',
  'itemId',
  'quantity',
  'unitPrice',
  'discountAmount',
  'tax',
  'net',
  'gross',
] as const;

/** Per-line tracking dimensions (Xero/QBO etc. — not Excel/Receipts). */
export const DRAWER_TRACKING_FIELD_ORDER = [
  'trackingOption1Id',
  'trackingOption2Id',
] as const;

export type DrawerTrackingFieldKey =
  (typeof DRAWER_TRACKING_FIELD_ORDER)[number];

export type TransactionEditDrawerPerLineFieldKey =
  | (typeof DRAWER_PER_LINE_FIELD_ORDER)[number]
  | DrawerTrackingFieldKey;

/**
 * Per-line column order for drawer table view (accountant flow).
 * Figures match transactions table: Qty → Unit Price → Discount → Tax → Net → Total.
 */
export const DRAWER_TABLE_PER_LINE_REQUIRED_ORDER = [
  'description',
  'accountCode',
  'taxName',
  'itemId',
  'quantity',
  'unitPrice',
  'discountAmount',
  'tax',
  'net',
  'gross',
] as const satisfies readonly TransactionEditDrawerPerLineFieldKey[];

/** Fallback width when a drawer table field has no explicit column width. */
export const DRAWER_TABLE_FALLBACK_COLUMN_WIDTH = 80;

/** Amount columns in drawer line table — fixed widths; table scrolls horizontally when needed. */
export const DRAWER_TABLE_AMOUNT_FIELD_KEYS = new Set<string>([
  'quantity',
  'unitPrice',
  'discountAmount',
  'tax',
  'net',
  'gross',
]);

/** Short header labels for drawer line table (compact columns). */
export const DRAWER_TABLE_HEADER_LABEL_I18N_KEYS: Partial<
  Record<string, string>
> = {
  quantity: 'transactions.quantityShort',
  unitPrice: 'transactions.unitPrice',
  gross: 'transactions.gross',
  discountAmount: 'transactions.discountAmountShort',
  net: 'transactions.net',
  tax: 'transactions.tax',
};

/** Fixed widths (px) for drawer line table — capped so amount/account columns stay readable. */
export const DRAWER_TABLE_COLUMN_WIDTHS: Partial<Record<string, number>> = {
  description: 140,
  accountCode: 84,
  itemId: 84,
  taxName: 84,
  quantity: 44,
  unitPrice: 54,
  discountAmount: 44,
  tax: 44,
  net: 54,
  gross: 54,
  trackingOption1Id: 72,
  trackingOption2Id: 72,
};

/** Index column width (row number only: 1, 2, …). */
export const DRAWER_TABLE_LINE_INDEX_COLUMN_WIDTH = 20;

/** Width of the outside-grid row-delete gutter (right of the table card). */
export const DRAWER_TABLE_ACTIONS_COLUMN_WIDTH = 22;

/** Floor when dragging to resize a drawer grid column. */
export const DRAWER_TABLE_COLUMN_RESIZE_MIN_WIDTH = 40;

/** Resolve stored/default width for one drawer grid column. */
export function resolveDrawerTableColumnWidth(
  fieldKey: string,
  customWidths?: Partial<Record<string, number>> | null
): number {
  const custom = customWidths?.[fieldKey];
  if (typeof custom === 'number' && Number.isFinite(custom)) {
    return Math.max(
      DRAWER_TABLE_COLUMN_RESIZE_MIN_WIDTH,
      Math.min(640, Math.round(custom))
    );
  }
  return (
    DRAWER_TABLE_COLUMN_WIDTHS[fieldKey] ?? DRAWER_TABLE_FALLBACK_COLUMN_WIDTH
  );
}

/** Minimum table width so grid view can scroll horizontally instead of squeezing columns. */
export function getDrawerLineTableMinWidth(
  fieldKeys: readonly string[],
  includeActionsColumn: boolean,
  customWidths?: Partial<Record<string, number>> | null
): number {
  const fieldWidth = fieldKeys.reduce((sum, key) => {
    return sum + resolveDrawerTableColumnWidth(key, customWidths);
  }, 0);
  return (
    DRAWER_TABLE_LINE_INDEX_COLUMN_WIDTH +
    fieldWidth +
    (includeActionsColumn ? DRAWER_TABLE_ACTIONS_COLUMN_WIDTH : 0)
  );
}

/** Per-line drawer order; tracking categories after Tax Name, before Item. */
export function buildDrawerPerLineFieldOrder(
  showTrackingCategoryColumns: boolean,
  allowedBaseKeys?: Set<string>
): TransactionEditDrawerPerLineFieldKey[] {
  const base = allowedBaseKeys
    ? DRAWER_PER_LINE_FIELD_ORDER.filter((k) => allowedBaseKeys.has(k))
    : [...DRAWER_PER_LINE_FIELD_ORDER];
  if (!showTrackingCategoryColumns) {
    return base as TransactionEditDrawerPerLineFieldKey[];
  }
  const result: TransactionEditDrawerPerLineFieldKey[] = [];
  for (const key of base) {
    result.push(key);
    if (key === 'taxName') {
      result.push('trackingOption1Id', 'trackingOption2Id');
    }
  }
  return result;
}

/**
 * Drawer line table order: tracking after Tax Name / before Item,
 * then Qty → Unit Price → Discount → Tax → Net → Total.
 */
export function buildDrawerTablePerLineFieldOrder(
  showTrackingCategoryColumns: boolean,
  allowedBaseKeys?: Set<string>
): TransactionEditDrawerPerLineFieldKey[] {
  const allow = (key: string) =>
    !allowedBaseKeys || allowedBaseKeys.has(key);

  const result: TransactionEditDrawerPerLineFieldKey[] = [];
  for (const key of DRAWER_TABLE_PER_LINE_REQUIRED_ORDER) {
    if (!allow(key)) continue;
    result.push(key);
    if (key === 'taxName' && showTrackingCategoryColumns) {
      result.push('trackingOption1Id', 'trackingOption2Id');
    }
  }
  return result;
}

/** Editable field keys used to detect if the user changed anything. */
export const EDITABLE_ROW_KEYS = [
  'transactionDate',
  'dueDate',
  'currency',
  'exchangeRate',
  'discountAmount',
  'sourceType',
  'transactionNumber',
  'reference',
  'gross',
  'net',
  'tax',
  'taxName',
  'taxRateId',
  'taxRate',
  'taxInclusive',
  'description',
  'contactType',
  'contactName',
  'vatNumber',
  'country',
  'accountId',
  'accountCode',
  'itemId',
  'quantity',
  'unitPrice',
] as const;

/** Special mass-update "field" that marks selected transactions as reviewed (no value input). */
export const MASS_UPDATE_FIELD_MARK_AS_REVIEWED = '__markAsReviewed';

export const TRACKING_OPTION_ID_FIELDS = [
  'trackingOption1Id',
  'trackingOption2Id',
] as const;

/** All tracking dimension fields sent on entity transaction save (Xero/QBO etc.). */
export const TRACKING_TRANSACTION_FIELDS = [
  'trackingCategory1Id',
  'trackingCategory1Name',
  'trackingCategory2Id',
  'trackingCategory2Name',
  'trackingOption1Id',
  'trackingOption1Name',
  'trackingOption2Id',
  'trackingOption2Name',
] as const;

/** Primary source type option values (persisted as-is). */
export const SOURCE_TYPE_VALUES = {
  SALES_INVOICE_RECEIPT: 'Sales - Invoice / Receipt',
  PURCHASE_SUPPLIER_BILL_PAYMENT: 'Purchase - Supplier Bill / Payment',
  SALES_CREDIT_NOTE: 'Sales Credit Note',
  PURCHASE_CREDIT_NOTE: 'Purchase Credit Note',
} as const;

const SOURCE_TYPE_I18N_KEYS: Record<string, string> = {
  [SOURCE_TYPE_VALUES.SALES_INVOICE_RECEIPT]:
    'organisation.transactions-source-type.sales-invoice-receipt',
  [SOURCE_TYPE_VALUES.PURCHASE_SUPPLIER_BILL_PAYMENT]:
    'organisation.transactions-source-type.purchase-supplier-bill-payment',
  [SOURCE_TYPE_VALUES.SALES_CREDIT_NOTE]:
    'organisation.transactions-source-type.sales-credit-note',
  [SOURCE_TYPE_VALUES.PURCHASE_CREDIT_NOTE]:
    'organisation.transactions-source-type.purchase-credit-note',
};

export function getSourceTypeOptions(t: TFunction = i18n.t.bind(i18n)) {
  return Object.values(SOURCE_TYPE_VALUES).map((value) => ({
    label: t(SOURCE_TYPE_I18N_KEYS[value]),
    value,
  }));
}

export function getSourceTypeDisplayLabel(
  value: string,
  t: TFunction = i18n.t.bind(i18n)
): string {
  const key = SOURCE_TYPE_I18N_KEYS[value];
  if (key) return t(key);
  return SOURCE_TYPE_DISPLAY[value] ?? value;
}

/** @deprecated Use getSourceTypeOptions(t) for translated labels. */
export const SOURCE_TYPE_OPTIONS = [
  { label: 'Sales - Invoice / Receipt', value: SOURCE_TYPE_VALUES.SALES_INVOICE_RECEIPT },
  {
    label: 'Purchase - Supplier Bill / Payment',
    value: SOURCE_TYPE_VALUES.PURCHASE_SUPPLIER_BILL_PAYMENT,
  },
  { label: 'Sales Credit Note', value: SOURCE_TYPE_VALUES.SALES_CREDIT_NOTE },
  { label: 'Purchase Credit Note', value: SOURCE_TYPE_VALUES.PURCHASE_CREDIT_NOTE },
];

/** Display labels for source type (new options + legacy ExcelTransactionSourceType for backward compat). */
export const SOURCE_TYPE_DISPLAY: Record<string, string> = {
  SALES_INVOICE_RECEIPT: 'Sales - Invoice / Receipt',
  PURCHASE_SUPPLIER_BILL_PAYMENT: 'Purchase - Supplier Bill / Payment',
  SALES_CREDIT_NOTE: 'Sales Credit Note',
  PURCHASE_CREDIT_NOTE: 'Purchase Credit Note',
  ...(ExcelTransactionSourceType as Record<string, string>),
};

export const NOT_CLICKED_ATTACHMENT = 'NOT_CLICKED_ATTACHMENT';

export const DEFAULT_PAGE_SIZE = 10;

/** Max characters for description in table cell; longer text is truncated with ellipsis and full text on hover. */
export const DESCRIPTION_DISPLAY_MAX_LENGTH = 50;
/** Max characters for tax name in table cell; longer text is truncated with ellipsis and full text on hover. */
export const TAX_NAME_DISPLAY_MAX_LENGTH = 25;
/** Max characters for account name in table cell; longer text is truncated with ellipsis and full text on hover. */
export const ACCOUNT_NAME_DISPLAY_MAX_LENGTH = 25;
/** Max characters for item name in table cell; longer text is truncated with ellipsis and full text on hover. */
export const ITEM_DISPLAY_MAX_LENGTH = 25;

/** Fallback English labels when i18n is unavailable (prefer ENTITY_TRANSACTION_COLUMN_I18N_KEYS). */
export const ENTITY_TRANSACTION_COLUMN_TITLES: Partial<Record<string, string>> =
  {
    net: 'Net',
    tax: 'Tax',
    sourceType: 'Source',
    transactionNumber: 'Inv/Bill No',
    gross: 'Total',
    vatNumber: 'Tax Number',
    transactionDate: 'Date',
    dueDate: 'Due Date',
    currency: 'Currency',
    exchangeRate: 'Exch. Rate',
    discountAmount: 'Discount',
    contactType: 'Is Customer/Supplier',
    accountCode: 'Account Name',
    itemId: 'Item',
    quantity: 'Quantity',
    unitPrice: 'Unit Price',
    taxInclusive: 'Tax Inclusive',
    description: 'Description',
    contactName: 'Name',
    country: 'Country',
    taxName: 'Tax Name',
    taxRate: 'Tax Rate',
    reference: 'Reference',
    [TRACKING_CATEGORY_1_COLUMN_KEY]: 'Tracking category 1',
    [TRACKING_CATEGORY_2_COLUMN_KEY]: 'Tracking category 2',
    uploadDate: 'Upload Date',
  };

/** i18n keys for entity transaction table / drawer column titles. */
export const ENTITY_TRANSACTION_COLUMN_I18N_KEYS: Record<string, string> = {
  net: 'transactions.net',
  tax: 'transactions.tax',
  sourceType: 'transactions.sourceType',
  transactionNumber: 'transactions.transactionNumber',
  gross: 'transactions.gross',
  vatNumber: 'transactions.vatNumber',
  transactionDate: 'transactions.transactionDate',
  dueDate: 'transactions.dueDate',
  currency: 'common.currency',
  exchangeRate: 'transactions.exchangeRateShort',
  discountAmount: 'transactions.discountAmountShort',
  contactType: 'transactions.contactType',
  accountCode: 'transactions.accountCode',
  accountId: 'transactions.accountCode',
  contactName: 'transactions.contactName',
  description: 'transactions.description',
  country: 'transactions.country',
  taxName: 'transactions.taxName',
  taxRate: 'transactions.taxRate',
  reference: 'organisation.reference',
  itemId: 'transactions.itemId',
  quantity: 'transactions.quantity',
  unitPrice: 'transactions.unitPrice',
  taxInclusive: 'transactions.taxInclusive',
  uploadDate: 'transactions.upload-date',
  [TRACKING_CATEGORY_1_COLUMN_KEY]: 'transactions.trackingCategory1',
  [TRACKING_CATEGORY_2_COLUMN_KEY]: 'transactions.trackingCategory2',
  trackingOption1Id: 'transactions.trackingCategory1',
  trackingOption2Id: 'transactions.trackingCategory2',
};

export const EDIT_DRAWER_FIELD_I18N_KEYS: Record<string, string> = {
  ...ENTITY_TRANSACTION_COLUMN_I18N_KEYS,
};

export function getEntityTransactionColumnTitle(
  dataKey: string,
  t: TFunction = i18n.t.bind(i18n)
): string {
  const i18nKey = ENTITY_TRANSACTION_COLUMN_I18N_KEYS[dataKey];
  if (i18nKey) return t(i18nKey);
  return ENTITY_TRANSACTION_COLUMN_TITLES[dataKey] ?? dataKey;
}

export function getEditDrawerLabel(
  fieldKey: string,
  t: TFunction = i18n.t.bind(i18n)
): string {
  const i18nKey = EDIT_DRAWER_FIELD_I18N_KEYS[fieldKey];
  if (i18nKey) return t(i18nKey);
  return EDIT_DRAWER_LABELS[fieldKey] ?? fieldKey;
}

/** Column keys shown in the table/drawer but not editable (system-populated). */
export const READ_ONLY_TRANSACTION_TAB_COLUMN_KEYS = new Set(['uploadDate']);

/**
 * Gross / Net are derived from quantity × unit price − discount and total − tax
 * for Xero / QuickBooks / Zoho / Receipts — not editable.
 * (Total formula also depends on taxInclusive.)
 */
export function isEntityTransactionColumnReadOnly(
  dataKey: string,
  deriveGrossNetFromQuantityUnitPrice = false
): boolean {
  if (READ_ONLY_TRANSACTION_TAB_COLUMN_KEYS.has(dataKey)) return true;
  if (
    deriveGrossNetFromQuantityUnitPrice &&
    (dataKey === 'gross' || dataKey === 'net')
  ) {
    return true;
  }
  return false;
}

/** Labels for edit drawer fields (same as transactions tab: short title or display name from mapping). */
export const EDIT_DRAWER_LABELS: Record<string, string> = (() => {
  const keyToDisplayName: Record<string, string> = {};
  Object.entries(VALIDATION_COLUMN_KEY_MAP).forEach(([name, dataKey]) => {
    if (dataKey !== 'errorReason') keyToDisplayName[dataKey as string] = name;
  });
  const labels: Record<string, string> = {};
  EDITABLE_ROW_KEYS.forEach((key) => {
    labels[key] =
      ENTITY_TRANSACTION_COLUMN_TITLES[key] ?? keyToDisplayName[key] ?? key;
  });
  DRAWER_TRACKING_FIELD_ORDER.forEach((key, idx) => {
    labels[key] =
      ENTITY_TRANSACTION_COLUMN_TITLES[
        idx === 0
          ? TRACKING_CATEGORY_1_COLUMN_KEY
          : TRACKING_CATEGORY_2_COLUMN_KEY
      ] ?? key;
  });
  READ_ONLY_TRANSACTION_TAB_COLUMN_KEYS.forEach((key) => {
    labels[key] =
      ENTITY_TRANSACTION_COLUMN_TITLES[key] ?? keyToDisplayName[key] ?? key;
  });
  return labels;
})();

/** Map edit drawer dataKey to mapImportFields label/tooltip key (for help icon). */
export const EDIT_DRAWER_TOOLTIP_KEY: Partial<Record<string, string>> = {
  transactionDate: 'transactionDate',
  dueDate: 'dueDate',
  sourceType: 'sourceType',
  transactionNumber: 'transactionNumber',
  reference: 'reference',
  gross: 'totalAmount',
  net: 'netAmount',
  taxName: 'taxName',
  taxRate: 'taxRate',
  currency: 'currency',
  exchangeRate: 'exchangeRate',
  discountAmount: 'discountAmount',
  description: 'description',
  contactType: 'isCustomerSupplier',
  contactName: 'name',
  vatNumber: 'vatNumber',
  country: 'country',
  accountCode: 'accountCode',
  itemId: 'itemId',
  taxInclusive: 'taxInclusive',
  accountType: 'accountType',
  trackingOption1Id: 'trackingOption1Id',
  trackingOption2Id: 'trackingOption2Id',
  uploadDate: 'uploadDate',
};

/** Optional fields (same as map import OPTIONAL_MAPPING_FIELDS). Drives optional ? tooltips in the edit drawer and "(optional)" in grid headers / column customize drawer. */
export const EDIT_DRAWER_OPTIONAL_KEYS = new Set<string>([
  'dueDate',
  'currency',
  'exchangeRate',
  'discountAmount',
  'transactionNumber',
  'reference',
  'description',
  'accountCode',
  'itemId',
  'taxInclusive',
  'trackingOption1Id',
  'trackingOption2Id',
  TRACKING_CATEGORY_1_COLUMN_KEY,
  TRACKING_CATEGORY_2_COLUMN_KEY,
]);

/** Xero / Zoho Books / QuickBooks: description and account name are required in the edit drawer. */
export function getEditDrawerOptionalKeys(
  providerName: string | null | undefined
): Set<string> {
  const keys = new Set(EDIT_DRAWER_OPTIONAL_KEYS);
  if (showsCoaAccountCodeColumn(providerName)) {
    keys.delete('description');
    keys.delete('accountCode');
  }
  return keys;
}

/** Width (px) shared by Total, Net and Tax amount columns so they stay the same size. */
export const ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH = 68;

/** File-upload side panel: default (+15 px vs legacy 345); user-drag left edge within min/max, saved per entity. */
export const FILE_PANEL_WIDTH_DEFAULT = 360;
export const FILE_PANEL_WIDTH_MIN = 280;
export const FILE_PANEL_WIDTH_MAX = 560;

/** Flex gap between main table column and side file-upload column in `transactions-tab-wrapper`. */
export const TRANSACTIONS_TAB_SIDE_GAP_PX = 25;

/** Matches horizontal padding on `.entity-transactions-tab` — aligns fixed AttachmentViewer with the padded content inset. */
export const ENTITY_TRANSACTIONS_TAB_HORIZONTAL_PADDING_PX = 24;

/** PWA: tighter horizontal inset for the transactions tab shell. */
export const ENTITY_TRANSACTIONS_PWA_MOBILE_PADDING_PX = 16;

/** PWA: compact column set (attachment hidden; amounts + primary labels). */
export const ENTITY_TRANSACTIONS_PWA_MOBILE_COLUMN_KEYS: readonly string[] = [
  'transactionDate',
  'contactName',
  'description',
  'net',
  'gross',
];

export function resolvePwaMobileTransactionColumnKeys(
  availableKeys: ReadonlySet<string>,
  showDeleted: boolean
): string[] {
  const keys: string[] = [];
  for (const key of ENTITY_TRANSACTIONS_PWA_MOBILE_COLUMN_KEYS) {
    if (availableKeys.has(key) && !keys.includes(key)) {
      keys.push(key);
    }
  }
  if (!keys.some((k) => k === 'contactName' || k === 'description')) {
    if (availableKeys.has('contactName')) {
      keys.unshift('contactName');
    } else if (availableKeys.has('description')) {
      keys.unshift('description');
    }
  }
  keys.push(
    showDeleted
      ? ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY
      : ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY
  );
  return keys;
}

/**
 * Minimum height for the table + file-panel row so short pages still fill the viewport.
 * The uploads file panel is fixed (like MappingForm); the page scrolls the table column.
 */
export const TRANSACTIONS_TAB_SPLIT_MIN_HEIGHT_CSS = 'calc(100vh - 172px)';

/** Fixed uploads file panel fallback offset (header 70px + entity tabs ~50px). */
export const TRANSACTIONS_FILE_PANEL_FIXED_TOP_PX = 120;

/** PWA Transactions file list: scrollable pages of this many documents. */
export const PWA_FILE_LIST_PAGE_SIZE = 10;

export const FILE_PANEL_RESIZE_STRIP_HIT_PX = 4;

/** Strip sits on file panel surface — subtle except on hover/active. */
export const FILE_PANEL_RESIZE_STRIP_BG_IDLE = 'var(--color-border, #dbeae3)';
export const FILE_PANEL_RESIZE_STRIP_BG_HOVER =
  'var(--file-panel-resize-strip-bg-hover, #ebf4f6)';
export const FILE_PANEL_RESIZE_STRIP_BG_ACTIVE =
  'var(--file-panel-resize-strip-bg-active, rgba(65, 170, 175, 0.12))';

/** Minimal column widths (px) so columns take the least possible space. */
export const ENTITY_TRANSACTION_COLUMN_MIN_WIDTHS: Partial<
  Record<string, number>
> = {
  transactionDate: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  /** Header "Due Date" + sort icon must stay on one line (DD/MM/YYYY cells ~80px). */
  dueDate: 98,
  sourceType: 58,
  transactionNumber: 52,
  reference: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  gross: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  net: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  tax: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  taxName: 90,
  accountCode: 90,
  itemId: 90,
  quantity: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  unitPrice: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  taxInclusive: 100,
  taxRate: 55,
  currency: 55,
  exchangeRate: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  discountAmount: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  description: 85,
  contactType: ENTITY_TRANSACTIONS_AMOUNT_COLUMN_WIDTH,
  contactName: 52,
  vatNumber: 85,
  country: 58,
  [TRACKING_CATEGORY_1_COLUMN_KEY]: 130,
  [TRACKING_CATEGORY_2_COLUMN_KEY]: 130,
  /** Header "Upload Date" + sort icon must stay on one line. */
  uploadDate: 110,
};

/** Currency / FX columns — shown before the pre-Total block (after tracking when present). */
export const MONETARY_AUXILIARY_COLUMN_KEYS = [
  'currency',
  'exchangeRate',
] as const;

/**
 * Columns immediately before the fixed amounts: Account Name → Name → Qty → Unit Price → Discount.
 * Quantity / unitPrice are present for Xero / QuickBooks / Zoho / Receipts.
 */
export const PRE_TOTAL_COLUMN_BLOCK_KEYS = [
  'accountCode',
  'contactName',
  'quantity',
  'unitPrice',
  'discountAmount',
] as const;

/** Fixed-right amount columns, display order: Tax → Net → Total. */
export const FIXED_AMOUNT_COLUMN_KEYS = ['tax', 'net', 'gross'] as const;

/**
 * Full trailing amount block for Xero / QuickBooks / Zoho / Receipts (table + column prefs).
 * Order: Qty → Unit Price → Discount → Tax → Net → Total.
 */
export const ENTITY_TRANSACTIONS_TAIL_AMOUNT_COLUMN_KEYS = [
  ...QUANTITY_UNIT_PRICE_COLUMN_KEYS,
  'discountAmount',
  ...FIXED_AMOUNT_COLUMN_KEYS,
] as const;

/** Tail amount columns in Customize: always visible (no hide checkbox), lockable, fixed order. */
export function getEntityTransactionsTailToggleableColumnKeys(
  showQuantityUnitPriceColumns: boolean
): string[] {
  if (!showQuantityUnitPriceColumns) return [];
  return [...QUANTITY_UNIT_PRICE_COLUMN_KEYS, 'discountAmount'];
}

/** Keys that must not be reordered in the column-customize drawer. */
export function getEntityTransactionsNonReorderableColumnKeys(
  showQuantityUnitPriceColumns: boolean
): string[] {
  return getEntityTransactionsTailToggleableColumnKeys(
    showQuantityUnitPriceColumns
  );
}

/** Keys that cannot be hidden in Customize (qty / unit price / discount). */
export function getEntityTransactionsAlwaysVisibleColumnKeys(
  showQuantityUnitPriceColumns: boolean
): string[] {
  return getEntityTransactionsTailToggleableColumnKeys(
    showQuantityUnitPriceColumns
  );
}

/** Ensure dueDate appears immediately after transactionDate (saved column prefs from before the column existed). */
export function injectDueDateColumnAfterTransactionDate(keys: string[]): string[] {
  if (keys.includes('dueDate')) return keys;
  const idx = keys.indexOf('transactionDate');
  if (idx === -1) return keys;
  const next = [...keys];
  next.splice(idx + 1, 0, 'dueDate');
  return next;
}

/** Place taxInclusive immediately after unitPrice (Receipts / Xero / Zoho / QuickBooks). */
export function injectTaxInclusiveColumnAfterUnitPrice(keys: string[]): string[] {
  const without = keys.filter((k) => k !== 'taxInclusive');
  const unitPriceIdx = without.indexOf('unitPrice');
  if (unitPriceIdx !== -1) {
    const next = [...without];
    next.splice(unitPriceIdx + 1, 0, 'taxInclusive');
    return next;
  }
  const discountIdx = without.indexOf('discountAmount');
  if (discountIdx !== -1) {
    const next = [...without];
    next.splice(discountIdx, 0, 'taxInclusive');
    return next;
  }
  // No qty/unit-price block (e.g. Excel) — omit the column.
  return without;
}

/**
 * Place tracking, then currency / exchange, then
 * Account Name → Name → Qty → Unit Price → Discount, then Tax → Net → Total.
 * Repositions existing keys from older prefs.
 */
export function repositionMonetaryColumnsBeforeGross(keys: string[]): string[] {
  const currencyFxPresent = MONETARY_AUXILIARY_COLUMN_KEYS.filter((k) =>
    keys.includes(k)
  );
  const currencyFxMissing = MONETARY_AUXILIARY_COLUMN_KEYS.filter(
    (k) => !keys.includes(k)
  );
  const trackingPresent = ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS.filter(
    (k) => keys.includes(k)
  );
  const preTotalPresent = PRE_TOTAL_COLUMN_BLOCK_KEYS.filter((k) =>
    keys.includes(k)
  );
  const amountPresent = FIXED_AMOUNT_COLUMN_KEYS.filter((k) => keys.includes(k));

  let next = keys.filter(
    (k) =>
      !MONETARY_AUXILIARY_COLUMN_KEYS.includes(
        k as (typeof MONETARY_AUXILIARY_COLUMN_KEYS)[number]
      ) &&
      !(PRE_TOTAL_COLUMN_BLOCK_KEYS as readonly string[]).includes(k) &&
      !(FIXED_AMOUNT_COLUMN_KEYS as readonly string[]).includes(k) &&
      !ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS.includes(k)
  );

  // Keep the trailing amount block where the first amount column used to sit.
  let insertAt = next.length;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (
      key === 'tax' ||
      key === 'net' ||
      key === 'gross' ||
      (PRE_TOTAL_COLUMN_BLOCK_KEYS as readonly string[]).includes(key) ||
      (MONETARY_AUXILIARY_COLUMN_KEYS as readonly string[]).includes(
        key as (typeof MONETARY_AUXILIARY_COLUMN_KEYS)[number]
      ) ||
      ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS.includes(key)
    ) {
      const before = keys
        .slice(0, i)
        .filter((k) => next.includes(k));
      insertAt = before.length;
      break;
    }
  }

  next.splice(
    insertAt,
    0,
    ...trackingPresent,
    ...currencyFxPresent,
    ...currencyFxMissing,
    ...preTotalPresent,
    ...amountPresent
  );
  return next;
}

/** Ensure uploadDate appears first (saved column prefs from before the column existed). */
export function injectUploadDateColumnFirst(keys: string[]): string[] {
  const without = keys.filter((k) => k !== 'uploadDate');
  if (keys.includes('uploadDate')) {
    return ['uploadDate', ...without];
  }
  return ['uploadDate', ...keys];
}

/** Patch saved column prefs when new default columns are added to the transactions tab. */
export function injectDefaultTransactionTabColumns(keys: string[]): string[] {
  return injectUploadDateColumnFirst(
    injectTaxInclusiveColumnAfterUnitPrice(
      repositionMonetaryColumnsBeforeGross(
        injectDueDateColumnAfterTransactionDate(keys)
      )
    )
  );
}

/** Add tracking category columns to saved prefs from before those columns existed. */
export function injectTrackingCategoryColumns(keys: string[]): string[] {
  const missing = ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS.filter(
    (k) => !keys.includes(k)
  );
  if (missing.length === 0) return keys;
  return repositionMonetaryColumnsBeforeGross([...keys, ...missing]);
}
